import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import * as ExcelJS from 'exceljs';
import { UserService } from 'src/app/service/user.service';
import { ImportData, UploadDataStatus, UserInfo } from '../../models/UserInfo';
import { Observable, Subject, map, switchMap, takeUntil } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { DatePipe } from '@angular/common';
import { AuthStateService } from 'src/app/service/auth-state.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-data-uploader',
  templateUrl: './data-uploader.component.html',
  styleUrls: ['./data-uploader.component.scss'],
})
export class DataUploaderComponent implements OnInit {
  uploadForm!: FormGroup;
  fileData: File | undefined;
  private destroy$ = new Subject<void>();
  allUploadedDataStatus = new MatTableDataSource<UploadDataStatus>([]);
  processFilePromise!: Promise<void>;
  displayedColumns: string[] = [
    'uploadedFileName',
    'uploadedBy',
    'uploadedDate',
    'uploadedStatus',
  ];
  uploadStatus: boolean | null = null;
  reviewUploadStatus: boolean = false;
  showtable: boolean = true;
  hasError = false;
  excelData: ImportData[] = [];
  errors: string[] = [];
  numberOfsameUsernames = 0;
  numberOfTotalRecords = 0;
  listOfNewUsernames: string[] = [];
  numberOfColumns = 29;
  userInfo: UserInfo[] = [];

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    private datePipe: DatePipe,
    private authStateService: AuthStateService
  ) {
    this.uploadForm = this.fb.group({
      uploadPath: ['', [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.userService.uploadDataStatus$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        if (data.length > 0){
          this.allUploadedDataStatus.data = this.sortUploadDataByDateDescending(data) || [];
        }
      });

      this.userService.userInfo$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.userInfo = data;
      });
  }

  xlsxInputChange(event: any) {
    const fileList: FileList = event.target.files;
    this.uploadStatus = null;
    if (fileList.length > 0) {
      this.fileData = fileList[0];
      this.uploadForm.get('uploadPath')!.setValue(this.fileData.name);
    }
  }

  formatDate(date: Date): string {
    const formattedDate = this.datePipe.transform(date, 'medium');
    return formattedDate || '';
  }

  sortUploadDataByDateDescending(data: UploadDataStatus[]): UploadDataStatus[] {
    return data.sort((a, b) => {
        return new Date(b.uploadedDate).getTime() - new Date(a.uploadedDate).getTime();
    });
  }

  isNWMOEmail(email: string): boolean {
    const domain = 'nwmo';
    const emailDomain = email.split('@')[1].split('.')[0];
    return emailDomain === domain;
  }

  uploadFile() {
    this.reset();
    const nwmoRestricted = (environment.NWMOrestricted == 'true');

    if (this.fileData) {
      const reader: FileReader = new FileReader();
      this.processFilePromise = new Promise<void>((resolve, reject) => {
        reader.onload = (e: any) => {
          const data: ArrayBuffer | string | null = e.target.result;
          if (data instanceof ArrayBuffer) {
            const workbook = new ExcelJS.Workbook();
            workbook.xlsx
              .load(data)
              .then(() => {
                const worksheet = workbook.getWorksheet("Statement");
                if (worksheet) {
                  const emailSet = new Set<string>();
                  const idSet = new Set<number>();
                  worksheet.eachRow(
                    { includeEmpty: false },
                    (row, rowNumber) => {
                      const rowData: Partial<ImportData> = {};
                      row.eachCell(
                        { includeEmpty: true },
                        (cell, colNumber) => {
                          var columnName = worksheet
                            .getCell(1, colNumber)
                            ?.value?.toString();
                          var cellValue = cell.value;
                          if (cell.result){
                            cellValue = cell.result;
                          }
                          if (columnName != cellValue) {
                            columnName = columnName?.replace(/\s/g, '');
                            switch (columnName) {
                              case 'NWMOEmail':
                                if (cellValue) {
                                  if (!emailSet.has(cellValue.toString())) {
                                    if (nwmoRestricted) {
                                      if(this.isNWMOEmail(cellValue.toString())) {
                                        rowData.Username = cellValue.toString();
                                        emailSet.add(cellValue.toString());
                                        rowData.IsAdmin = this.checkIsAdmin(rowData.Username);
                                        rowData.IsLocked = this.checkIsLocked(rowData.Username);
                                      } else {
                                        this.errors.push(
                                          `Email not valid ${rowNumber}: ${cellValue}`
                                        );
                                        this.hasError = true;
                                      }
                                    }
                                    else {
                                      rowData.Username = cellValue.toString();
                                      emailSet.add(cellValue.toString());
                                      rowData.IsAdmin = this.checkIsAdmin(rowData.Username);
                                      rowData.IsLocked = this.checkIsLocked(rowData.Username);
                                    }
                                  } else {
                                    this.errors.push(
                                      `Duplicate email found on row ${rowNumber}: ${cellValue}`
                                    );
                                    this.hasError = true;
                                  }
                                } else {
                                  this.errors.push(
                                    `Email cannot be null on row ${rowNumber}`
                                  );
                                  this.hasError = true;
                                }
                                break;
                              case 'MemberName':
                                if (cellValue) {
                                  rowData.MemberName = cellValue.toString();
                                } else {
                                  this.errors.push(
                                    `Member Name cannot be null on row ${rowNumber}`
                                  );
                                  this.hasError = true;
                                }
                                break;
                              case 'Representation':
                                if (
                                  cellValue &&
                                  [
                                    'Management',
                                    'Society',
                                    'PWU',
                                    'representation',
                                  ].includes(cellValue.toString())
                                ) {
                                  rowData.Representation = cellValue.toString();
                                } else {
                                  this.errors.push(
                                    `Invalid Representation on row ${rowNumber}`
                                  );
                                  this.hasError = true;
                                }
                                break;
                              case 'IDNumber':
                                if (
                                  cellValue &&
                                  /^\d{6}$/.test(cellValue.toString())
                                ) {
                                  const idNumber = parseInt(
                                    cellValue.toString(),
                                    10
                                  );
                                  if (!idSet.has(idNumber)) {
                                    rowData.IDNumber = idNumber;
                                    idSet.add(idNumber);
                                  } else {
                                    this.errors.push(
                                      `Duplicate IDNumber found on row ${rowNumber}: ${idNumber}`
                                    );
                                    this.hasError = true;
                                  }
                                } else {
                                  if (columnName !== 'IDNumber') {
                                    this.errors.push(
                                      `Invalid IDNumber on row ${rowNumber}`
                                    );
                                    this.hasError = true;
                                  }
                                }
                                break;
                              case 'CalcDate':
                              case 'DOB':
                              case 'DOE':
                              case 'DOPE':
                              case 'ERD':
                              case 'EURD':
                              case 'NRD':
                                if (!cellValue) {
                                  this.errors.push(
                                    `${columnName} cannot be null on row ${rowNumber}`
                                  );
                                  this.hasError = true;
                                } else {
                                  rowData[columnName] =
                                    cellValue instanceof Date
                                      ? cellValue
                                      : new Date(cellValue.toString());
                                }
                                break;
                              case 'MarriageBreakdownAmountowingtoexspouse':
                                if (!cellValue) {
                                  this.errors.push(
                                    `${columnName} cannot be null on row ${rowNumber}`
                                  );
                                  this.hasError = true;
                                } else {
                                  const validPattern = /^[0-9]+(\.[0-9]+)?$|^n\/a$/;
                                  var parsedValue = cellValue
                                    .toString()
                                    .replace(/[$,]/g, '');
                                  if (validPattern.test(parsedValue)) {
                                    rowData[columnName] = parsedValue;
                                  }
                                  else {
                                    this.errors.push(
                                      `${columnName} must be a positive value or 'n/a' on row ${rowNumber}`
                                    );
                                    this.hasError = true;
                                  }
                                  rowData[columnName] = cellValue.toString();
                                }
                                break;
                              case 'ESPSEURD':
                              case 'ESPSStart':
                              case 'SPSDOB':
                                if (!cellValue) {
                                  this.errors.push(
                                    `${columnName} cannot be null on row ${rowNumber}`
                                  );
                                  this.hasError = true;
                                } else {
                                  rowData[columnName] = cellValue.toString();
                                }
                                break;
                              case 'SPP?':
                                if (!cellValue) {
                                  this.errors.push(
                                    `${columnName} cannot be null on row ${rowNumber}`
                                  );
                                  this.hasError = true;
                                } else {
                                  const validPattern = /^[0-9]+(\.[0-9]+)?$|^[NS]$/;
                                  var parsedValue = cellValue
                                    .toString()
                                    .replace(/[$,]/g, '');
                                  if (validPattern.test(parsedValue)) {
                                    rowData['SPP'] = parsedValue;
                                  }
                                  else {
                                    this.errors.push(
                                      `${columnName} must be a positive value or N or S on row ${rowNumber}`
                                    );
                                    this.hasError = true;
                                  }
                                }
                                break;
                              case 'Cred':
                              case 'Elig':
                              case 'High3':
                              case 'High5':
                              case 'YMPE3':
                                if (!cellValue) {
                                  this.errors.push(
                                    `${columnName} cannot be null on row ${rowNumber}`
                                  );
                                  this.hasError = true;
                                } else {
                                  var parsedValue = cellValue
                                    .toString()
                                    .replace(/[$,]/g, '');
                                  if (parseFloat(parsedValue) < 0) {
                                    this.errors.push(
                                      `${columnName} cannot be a negative value on row ${rowNumber}`
                                    );
                                    this.hasError = true;
                                  }
                                  
                                  rowData[columnName] = !isNaN(
                                    parseFloat(parsedValue.toString())
                                  )
                                    ? parseFloat(parsedValue.toString())
                                    : undefined;
                                }
                                break;
                              case 'Total_Life_Unreduced':
                              case 'ESPS_Life_Unreduced':
                                if (!cellValue) {
                                  rowData[columnName] = 0;
                                } else {
                                  var parsedValue = cellValue
                                    .toString()
                                    .replace(/[$,]/g, '');
                                  if (parseFloat(parsedValue) < 0) {
                                    this.errors.push(
                                      `${columnName} cannot be a negative value on row ${rowNumber}`
                                    );
                                    this.hasError = true;
                                  }
                                  rowData[columnName] = !isNaN(
                                    parseFloat(parsedValue.toString())
                                  )
                                    ? parseFloat(parsedValue.toString())
                                    : undefined;
                                }
                                break;
                              case 'LifetimePension(Statement)':
                                if (!cellValue) {
                                  rowData['Lifetime_Pension_Statement'] = 0;
                                } else {
                                  var parsedValue = cellValue
                                    .toString()
                                    .replace(/[$,]/g, '');
                                  if (parseFloat(parsedValue) < 0) {
                                    this.errors.push(
                                      `${columnName} cannot be a negative value on row ${rowNumber}`
                                    );
                                    this.hasError = true;
                                  }
                                  rowData['Lifetime_Pension_Statement'] = !isNaN(
                                    parseFloat(parsedValue.toString())
                                  )
                                    ? parseFloat(parsedValue.toString())
                                    : undefined;
                                }
                                break;
                              case 'BridgePension(Statement)':
                                if (!cellValue) {
                                  rowData['Bridge_Pension_Statement'] = 0;
                                } else {
                                  var parsedValue = cellValue
                                    .toString()
                                    .replace(/[$,]/g, '');
                                  if (parseFloat(parsedValue) < 0) {
                                    this.errors.push(
                                      `${columnName} cannot be a negative value on row ${rowNumber}`
                                    );
                                    this.hasError = true;
                                  }
                                  rowData['Bridge_Pension_Statement'] = !isNaN(
                                    parseFloat(parsedValue.toString())
                                  )
                                    ? parseFloat(parsedValue.toString())
                                    : undefined;
                                }
                                break;
                              case 'ESPSHigh3':
                                if (!cellValue) {
                                  rowData[columnName] = 0;
                                } else {
                                  var parsedValue = cellValue
                                    .toString()
                                    .replace(/[$,]/g, '');
                                  if (parseFloat(parsedValue) < 0) {
                                    this.errors.push(
                                      `${columnName} cannot be a negative value on row ${rowNumber}`
                                    );
                                    this.hasError = true;
                                  }
                                  rowData[columnName] = !isNaN(
                                    parseFloat(parsedValue.toString())
                                  )
                                    ? parseFloat(parsedValue.toString())
                                    : undefined;
                                }
                                break;
                              case 'Marrbkdn?':
                                if (cellValue === 'Y') {
                                  rowData['Marrbkdn'] = true;
                                } else if (cellValue === 'N') {
                                  rowData['Marrbkdn'] = false;
                                } else {
                                  this.errors.push(
                                    `${columnName} must be 'Y' or 'N' on row ${rowNumber}`
                                  );
                                  this.hasError = true;
                                }
                                break;
                              default:
                                break;
                            }
                          }
                        }
                      );
                      if (rowNumber != 1) {
                        if (
                          !this.hasError &&
                          Object.keys(rowData).length === this.numberOfColumns
                        ) {
                          this.excelData.push(rowData as ImportData);
                        }
                        else{
                          this.hasError = true;
                        }
                      }
                    }
                  );
                  resolve();
                } else {
                  reject(new Error('Worksheet not found in workbook.'));
                }
              })
              .catch((error) => {
                reject(error);
              });
          } else {
            reject(new Error('Failed to load file as ArrayBuffer.'));
          }
        };
      });

      reader.readAsArrayBuffer(this.fileData);

      this.processFilePromise
        .then(() => {
          if (!this.hasError) {
            this.showtable = false;
            this.reviewUploadStatus = true;
            this.numberOfTotalRecords = this.excelData.length;
            this.userService
              .getMembers()
              .pipe(takeUntil(this.destroy$))
              .subscribe((data) => {
                this.excelData.forEach((row) => {
                  const matchingUsername = data.find(
                    (user) => user.username === row.Username
                  );
                  if (matchingUsername) {
                    this.numberOfsameUsernames++;
                  } else {
                    this.listOfNewUsernames.push(row.Username);
                  }
                });
              });
          } else {
            this.uploadStatus = false;
            this.updateTable(false);
          }
        });
    }
  }

  saveUpload() {
    this.userService.upsertMemberInfo(this.excelData).subscribe(
      () => {
        this.reviewUploadStatus = false;
        this.showtable = true;
        this.uploadStatus = true;
        this.updateTable(true);
      }
    );
  }

  checkIsAdmin(username: string): boolean {
    const user = this.userInfo.find((user) => user.username === username);
    return user ? user.isAdmin : false;
  }

  checkIsLocked(username: string): boolean {
    const user = this.userInfo.find((user) => user.username === username);
    return user ? user.isLocked : false;
  }

  updateTable(uploadStatus: boolean) {
    var currentUsername = this.authStateService.activeAccount?.username || '';

    this.userService
      .getSingleUserInfo(currentUsername)
      .subscribe((userInfo: UserInfo | undefined) => {
        if (userInfo) {
          var uploadedBy = userInfo.memberName + ', ' + userInfo.username;
          const uploadDataStatus: UploadDataStatus = {
            uploadedFilename: this.fileData?.name || 'N/A',
            uploadedBy: uploadedBy,
            uploadedDate: new Date(),
            uploadedStatus: uploadStatus,
          };
          this.userService
            .insertDataUploadStatus(uploadDataStatus)
            .pipe(
              switchMap(() => {
                this.userService.setCurrentUser(currentUsername);
                return this.userService.getAllDataUploadStatus()}
              ),
              takeUntil(this.destroy$)
            )
            .subscribe(() => {
              this.userService
                .getAllDataUploadStatus()
                .pipe(takeUntil(this.destroy$))
                .subscribe();
            });
        }
      });
  }

  cancel() {
    this.showtable = true;
    this.reviewUploadStatus = false;
  }

  reset() {
    this.showtable = true;
    this.reviewUploadStatus = false;
    this.uploadStatus = null;
    this.numberOfTotalRecords = 0;
    this.numberOfsameUsernames = 0;
    this.listOfNewUsernames = [];
    this.excelData = [];
  }
}
